import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, Scrollbar, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/scrollbar";
import HoverMagnifier from "./HoverMagnifier";

interface Slide {
  img: string;
}

interface SwipperProps {
  slidesData: Slide[];
  // Opt-in hover loupe on the main slide. Defaults off so existing call
  // sites (Create / Update listing previews) keep their current behavior.
  enableMagnifier?: boolean;
  // Touch-tap fallback: invoked with the tapped image URL so the parent
  // can open a fullscreen lightbox.
  onMainImageTap?: (src: string) => void;
}

const THUMB_SIZE = 100;
const THUMB_GAP = 10;
// Extra room reserved on the right of the thumbs strip for the
// Swiper scrollbar rail, so the last visible thumbnail isn't covered
// by it. The rail itself is 4px wide; this leaves a small margin.
const SCROLLBAR_TRACK = 14;

const SliderWithThumbVertical: React.FC<SwipperProps> = ({
  slidesData,
  enableMagnifier = false,
  onMainImageTap,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>();
  const mainSwiperRef = useRef<any>(null);

  return (
    <div className="slider-container verticalSlider">
      <Swiper
        ref={mainSwiperRef}
        modules={[Thumbs]}
        thumbs={{ swiper: thumbsSwiper }}
        className="main-swiper"
        spaceBetween={10}
      >
        {slidesData.map((slide, index) => (
          <SwiperSlide key={index}>
            {/* Rendered as background-image div (not <img>) so
                boneyard's snapshot extractor doesn't apply its
                "square media element → 50% border-radius" heuristic
                (boneyard-js/dist/extract.js:50). With <img>, the
                captured bone forces circle radius regardless of the
                actual borderRadius. The HoverMagnifier branch preserves
                the same div-based markup. */}
            {enableMagnifier ? (
              <HoverMagnifier
                src={slide?.img}
                alt={`Slide ${index}`}
                style={{
                  width: "100%",
                  height: 600,
                  maxHeight: 600,
                  borderRadius: 10,
                }}
                onTap={
                  onMainImageTap
                    ? () => onMainImageTap(slide?.img)
                    : undefined
                }
              />
            ) : (
              <div
                role="img"
                aria-label={`Slide ${index}`}
                style={{
                  backgroundColor: "#eee",
                  backgroundImage: `url(${slide?.img})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center center",
                  width: "100%",
                  height: 600,
                  maxHeight: 600,
                  borderRadius: 10,
                }}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <Swiper
        modules={[Thumbs, Scrollbar, Mousewheel]}
        watchSlidesProgress
        onSwiper={setThumbsSwiper}
        spaceBetween={THUMB_GAP}
        slidesPerView={"auto"}
        direction={"vertical"}
        scrollbar={{ draggable: true, hide: false }}
        mousewheel
        className="thumbs-swiper"
        style={{
          height: 600,
          width: THUMB_SIZE + SCROLLBAR_TRACK,
        }}
      >
        {slidesData.map((slide, index) => (
          <SwiperSlide
            key={index}
            style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
          >
            <div
              role="img"
              aria-label={`Thumbnail ${index}`}
              style={{
                backgroundColor: "#eee",
                backgroundImage: `url(${slide?.img})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                borderRadius: 10,
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SliderWithThumbVertical;
